import type { CSSProperties } from 'preact'
import { useState } from 'preact/hooks'

import type {
  CookieCategory,
  CookieRecord,
} from '../../../../services/cookieTranslationService/types'
import {
  getCategoryDescription,
  getCategoryTitle,
} from '../../../core/consentConfig'

import type { CookieConsentTranslator } from '#src/assets/lib/i18n.ts'
import { ChevronIcon } from '#src/assets/ui/icons/ChevronIcon.tsx'
import { CookieControllerList } from '#src/assets/ui/overview/components/CookieControllerList.tsx'
import { ToggleSwitch } from '#src/assets/ui/preferences/components/ToggleSwitch.tsx'
import { Paragraph } from '#src/assets/ui/shared/components/Paragraph.tsx'
import { uiTokens } from '#src/assets/ui/shared/styles.ts'

export function CookieCategoryRow({
  category,
  cookies,
  fallbackDomain,
  t,
  checked,
  readOnly,
  onToggle,
}: {
  category: CookieCategory
  cookies: Array<CookieRecord>
  fallbackDomain: string
  t: CookieConsentTranslator
  checked: boolean
  readOnly?: boolean
  onToggle?: (checked: boolean) => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const title = getCategoryTitle({ category, t })
  const description = getCategoryDescription({ category, t })

  return (
    <div style={rowStyle}>
      <div style={rowHeaderStyle}>
        <button
          type="button"
          style={chevronButtonStyle}
          aria-expanded={isExpanded}
          aria-label={title}
          onClick={event => {
            event.stopPropagation()
            setIsExpanded(prev => !prev)
          }}
        >
          <span
            style={{
              display: 'flex',
              color: uiTokens.colors.gray900,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ChevronIcon />
          </span>
        </button>

        <button
          type="button"
          style={headerStyle}
          onClick={() => setIsExpanded(prev => !prev)}
        >
          <span style={titleStyle}>{title}</span>
        </button>

        <div style={toggleWrapperStyle}>
          <ToggleSwitch
            checked={checked}
            disabled={readOnly}
            onChange={onToggle}
          />
        </div>
      </div>

      <ExpandableSection
        isExpanded={isExpanded}
        description={description}
        cookies={cookies}
        fallbackDomain={fallbackDomain}
        t={t}
      />
    </div>
  )
}

function ExpandableSection({
  isExpanded,
  description,
  cookies,
  fallbackDomain,
  t,
}: {
  isExpanded: boolean
  description: string
  cookies: Array<CookieRecord>
  fallbackDomain: string
  t: CookieConsentTranslator
}) {
  if (!isExpanded) {
    return null
  }

  return (
    <div style={detailsStyle}>
      <Paragraph variant="caption" style={{ margin: '0 0 16px' }}>
        {description}
      </Paragraph>
      {cookies.length > 0 ? (
        <CookieControllerList
          cookies={cookies}
          fallbackDomain={fallbackDomain}
          t={t}
        />
      ) : null}
    </div>
  )
}

const rowStyle: CSSProperties = {
  borderRadius: uiTokens.radius.sm,
  overflow: 'hidden',
  background: uiTokens.colors.gray125,
}

const rowHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: '72px',
  padding: '0 16px 0 24px',
  boxSizing: 'border-box',
}

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  gap: '12px',
  padding: '14px 16px',
  width: '100%',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
}

const chevronButtonStyle: CSSProperties = {
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  border: 'none',
  background: uiTokens.colors.toggleTrackDisabled,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
  cursor: 'pointer',
}

const titleStyle: CSSProperties = {
  flex: 1,
  fontSize: '0.95em',
  fontWeight: '700',
  color: uiTokens.colors.gray900,
}

const toggleWrapperStyle: CSSProperties = {
  display: 'flex',
  paddingLeft: '16px',
}

const detailsStyle: CSSProperties = {
  padding: '0 24px 16px',
}
